// Which settings each node-attribute type can carry, as the server's `NodeAttributeDefinition` constructor and save path decide it.
// Moved here from web-admin's `attributeViews.ts` (KEY-560) so the user app's builder offers and clears settings by the same rules
// the admin editor does; web-admin re-exports them from their old path.

/**
 * The types a dial-wide field can be placed on other nodes with (KEY-378). The file-backed types can't:
 * their values name documents validated against one exact node under one exact attribute
 * (DocumentsBelongToNodeAsync), and a dial-canonical file has no node — so the server drops a placement
 * on them and the editor doesn't offer the picker, exactly as {@link attributeTypeSupportsDefault}
 * hides the default-value field.
 */
export function attributeTypeSupportsPlacement(type: string): boolean {
  return type !== "Document" && type !== "Image" && type !== "Video";
}

/**
 * The types that can carry an authored default value (KEY-286). The file-backed types name an instance's
 * own uploads and `User` one of its members, neither of which version-owned template data can pick — the
 * server drops a default on any of them, so the editor doesn't offer the field. `type` is stored as a bare
 * string, so an unrecognised/future value simply doesn't get the field either.
 */
export function attributeTypeSupportsDefault(type: string): boolean {
  return type === "Text" || type === "Date" || type === "RichText" || type === "List";
}

/**
 * The types that carry an authored options list (KEY-481, KEY-568): `List` for its answer list, `Table`
 * for its column headers — same storage, same options block in the card. A predicate rather than a bare
 * equality check for the same reason the two above are: the checks are scattered across the card (the
 * options block, the type-switch clear, the default-value control, the issue rules), and one of them
 * silently disagreeing with the server is how a feature half-works.
 */
export function attributeTypeSupportsOptions(type: string): boolean {
  return type === "List" || type === "Table";
}

/**
 * The one type whose options carry the open/closed switch (KEY-481). `Table` (KEY-568) carries options
 * too — its column headers — but has no "choice" to leave open: every row is somebody's own typed data,
 * not a pick from the header list, so `allowUserValues` is meaningless for it and the server drops it.
 */
export function attributeTypeSupportsUserValues(type: string): boolean {
  return type === "List";
}

/**
 * The types a GROUP dial may answer on one of its children (KEY-483). The file-backed types are excluded
 * because their value is a set of document ids scoped to ONE instance — projected onto the group they
 * would name files the group cannot serve — and `List` because two dials' answer lists are unrelated, so
 * a fed answer could be off-list on the group's own declaration: a value the group's own write path would
 * refuse if anybody typed it, shown as though it were fine.
 *
 * A predicate for the same reason the three above are: the server drops a source on any other type, and a
 * card that offered the control anyway would let an admin configure something that silently vanished on
 * save.
 */
export function attributeTypeSupportsChildSource(type: string): boolean {
  return type === "Text" || type === "Date" || type === "RichText" || type === "Email" || type === "Phone" || type === "User";
}

/**
 * The types that may hold more than one value when the author ticks "allow more than one" (KEY-247,
 * KEY-PEOPLE): several files, several images, or several people. Re-exported from the api-client's
 * `attributeTypeSupportsMultiple`, which the workspace's Person field reads too, so the editor's checkbox,
 * the builder's clear-on-retype and the picker's shape agree by construction.
 */
export { attributeTypeSupportsMultiple } from "@keydials/api-client";
